/**
 * TypeAttackersCard — the best attackers of one type by effective DPS (plan phase 5, decision 0023).
 *
 * The backend's ``type_attackers_card`` carries the type's table: role, Pokémon,
 * the moves it needs with their flags (CD, ETM, form, SM, P), eDPS and the
 * percentage of the type's budget baseline, dated to the month the table is
 * as of. Rendered by CardRenderer.
 */
import type { VisualPayload } from "../../types";
import CardShell from "./_CardShell";
import { TYPE_BADGE_COLORS } from "../../utils/typeColors";

interface Props { payload: VisualPayload }
interface Row { role: string; name: string; moves: string[]; flags: string[]; edps: number; base_pct: number | null }

const ROLE_STYLE: Record<string, string> = {
  Mega: "bg-blue-500/15 text-blue-300", Shadow: "bg-violet-500/15 text-violet-300",
  Regular: "bg-emerald-500/15 text-emerald-300", Budget: "bg-white/10 text-white/60",
};

export default function TypeAttackersCard({ payload }: Props) {
  const sec = payload.sections.find((s) => s.section === "attackers") as Record<string, unknown> | undefined;
  if (!sec) {
    return (
      <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
        <p className="text-sm text-white/40 py-4 text-center">No attackers table on file.</p>
      </CardShell>
    );
  }
  const rows = Array.isArray(sec.rows) ? (sec.rows as Row[]) : [];
  const type = typeof sec.type === "string" ? sec.type : "";
  const typeCls = TYPE_BADGE_COLORS[type.toLowerCase()] ?? "bg-white/10 text-white/60";
  const meanings = (sec.flag_meanings ?? {}) as Record<string, string>;
  const usedFlags = Array.from(new Set(rows.flatMap((r) => r.flags ?? [])));
  return (
    <CardShell title={payload.title} source={payload.data_source} timestamp={payload.data_timestamp} sourceUrl={payload.source_url} shareUrl={payload.share_url} snapshotPayload={payload.snapshot_eligible ? payload : undefined}>
      <div className="space-y-3">
        <div className="flex items-center gap-2">
          <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${typeCls}`}>{type}</span>
          <span className="text-[10px] text-white/30 uppercase tracking-wide">Effective DPS at level 40{typeof sec.as_of === "string" ? ` · as of ${sec.as_of}` : ""}</span>
        </div>
        <div className="space-y-1.5">
          {rows.map((r) => (
            <div key={`${r.role}-${r.name}`} className="rounded-lg border border-white/[0.07] px-3 py-2">
              <div className="flex items-center justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`text-[10px] px-1.5 py-0.5 rounded font-semibold ${ROLE_STYLE[r.role] ?? ROLE_STYLE.Budget}`}>{r.role}</span>
                  <span className="text-sm text-white/90 truncate">{r.name}</span>
                </div>
                <span className="text-sm font-mono text-white/80">{r.edps.toFixed(1)}{r.base_pct != null ? <span className="text-white/35 text-[11px]"> · {r.base_pct}%</span> : null}</span>
              </div>
              <p className="text-[11px] text-white/50 mt-0.5">{(r.moves ?? []).join(", ")}{(r.flags ?? []).length ? ` (${r.flags.join(", ")})` : ""}</p>
            </div>
          ))}
        </div>
        {usedFlags.length > 0 && (
          <p className="text-[10px] text-white/35">{usedFlags.map((f) => `${f}: ${meanings[f] ?? f}`).join(" · ")}</p>
        )}
      </div>
    </CardShell>
  );
}
